// src/utils/Onboarding/MediaPipe.js

import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';

/**
 * MEDIAPIPE FACE DETECTION UTILITIES
 * 
 * Handles MediaPipe face detection setup and processing for onboarding images.
 * Optimized for fast on-device performance with proper error handling.
 * 
 * FEATURES:
 * - Face detection with bounding boxes
 * - 6 facial keypoints (eyes, nose, mouth, ear tragions)
 * - Normalized-to-canvas coordinate conversion (handles both normalized and pixel inputs)
 * - Singleton pattern for efficient resource usage
 * - Error handling and fallback support
 * 
 * USAGE:
 * ```javascript
 * import { initializeMediaPipe, detectFaces } from './MediaPipe';
 * 
 * await initializeMediaPipe();
 * const results = await detectFaces(imageElement);
 * ```
 */

// Singleton face detector instance
let faceDetector = null;
let isInitializing = false;
let initializationError = null;

/**
 * Initialize MediaPipe Face Detector
 * Uses singleton pattern to avoid multiple initializations
 */
export async function initializeMediaPipe() {
  // Return existing detector if already initialized
  if (faceDetector) {
    return faceDetector;
  }
  
  // Wait if currently initializing
  if (isInitializing) {
    while (isInitializing) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return faceDetector;
  }
  
  try {
    isInitializing = true;
    
    // Suppress WASM internal logs during initialization
    const originalLog = console.log;
    const originalWarn = console.warn;
    console.log = (...args) => {
      const msg = args[0];
      if (typeof msg === 'string' && (msg.includes('GL version') || msg.includes('Graph successfully') || msg.includes('gl_context'))) return;
      originalLog.apply(console, args);
    };
    console.warn = (...args) => {
      const msg = args[0];
      if (typeof msg === 'string' && (msg.includes('OpenGL error checking') || msg.includes('gl_context'))) return;
      originalWarn.apply(console, args);
    };
    
    // Load MediaPipe WASM files - version MUST match package.json
    const vision = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm'
    );
    
    // Create face detector with optimized settings
    faceDetector = await FaceDetector.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite',
        delegate: 'GPU' // Use GPU acceleration when available
      },
      runningMode: 'IMAGE',
      minDetectionConfidence: 0.5, // Lower threshold for better detection
      minSuppressionThreshold: 0.3
    });
    
    // Restore console
    console.log = originalLog;
    console.warn = originalWarn;
    
    initializationError = null;
    
    return faceDetector;
    
  } catch (error) {
    console.error('Failed to initialize MediaPipe Face Detector:', error);
    initializationError = error;
    faceDetector = null;
    throw error;
  } finally {
    isInitializing = false;
  }
}

/**
 * Detect faces in an image
 * @param {HTMLImageElement|HTMLCanvasElement} imageElement - Image to process
 * @returns {Promise<Object>} Face detection results
 */
export async function detectFaces(imageElement) {
  try {
    // Ensure MediaPipe is initialized
    if (!faceDetector) {
      await initializeMediaPipe();
    }
    
    if (!faceDetector) {
      throw new Error('MediaPipe Face Detector not available');
    }
    
    // Validate input
    if (!imageElement || !imageElement.complete) {
      throw new Error('Invalid or incomplete image element');
    }
    
    const startTime = performance.now();
    
    // Run face detection
    const results = faceDetector.detect(imageElement);
    
    const endTime = performance.now();
    
    return {
      success: true,
      detections: results.detections,
      processingTime: endTime - startTime,
      imageSize: {
        width: imageElement.naturalWidth || imageElement.width,
        height: imageElement.naturalHeight || imageElement.height
      }
    };
    
  } catch (error) {
    console.error('Face detection failed:', error);
    return {
      success: false,
      error: error.message,
      detections: []
    };
  }
}

/**
 * Convert normalized MediaPipe coordinates to pixel coordinates
 * @param {number} normalizedX - X coordinate (0-1)
 * @param {number} normalizedY - Y coordinate (0-1) 
 * @param {number} imageWidth - Image width in pixels
 * @param {number} imageHeight - Image height in pixels
 * @returns {Object} Pixel coordinates {x, y}
 */
export function normalizedToPixel(normalizedX, normalizedY, imageWidth, imageHeight) {
  return {
    x: normalizedX * imageWidth,
    y: normalizedY * imageHeight
  };
}

/**
 * INTERNAL: Safely derive a normalized (0-1) coordinate from either
 * already-normalized inputs or pixel-space inputs.
 */
function toNormalizedCoord(x, y, imgWidth, imgHeight) {
  // If both within [0,1], treat as normalized (Tasks API often does this)
  const looksNormalized = x >= 0 && x <= 1 && y >= 0 && y <= 1;
  if (looksNormalized) {
    return {
      nx: Math.min(1, Math.max(0, x)),
      ny: Math.min(1, Math.max(0, y))
    };
  }
  // Otherwise assume pixels and normalize
  return {
    nx: Math.min(1, Math.max(0, x / imgWidth)),
    ny: Math.min(1, Math.max(0, y / imgHeight))
  };
}

/**
 * Convert MediaPipe face detection results to canvas drawing data
 * @param {Array} detections - MediaPipe face detections
 * @param {number} canvasWidth - Canvas width in pixels
 * @param {number} canvasHeight - Canvas height in pixels
 * @param {Object} drawBounds - Image draw bounds in canvas
 * @param {Object} originalImageSize - Original image dimensions {width, height}
 * @returns {Array} Canvas drawing data for each face
 */
export function convertDetectionsToCanvas(detections, canvasWidth, canvasHeight, drawBounds, originalImageSize) {
  if (!detections || !Array.isArray(detections)) {
    return [];
  }
  
  const { drawX, drawY, drawWidth, drawHeight } = drawBounds;
  const { width: imgWidth, height: imgHeight } = originalImageSize;
  
  return detections.map((detection, index) => {
    const face = {
      id: index,
      confidence: detection.categories?.[0]?.score || 0,
      boundingBox: null,
      keypoints: []
    };
    
    // Convert bounding box - MediaPipe returns PIXEL coordinates for BlazeFace bbox
    if (detection.boundingBox) {
      const bbox = detection.boundingBox;
      
      // STEP 1: Normalize pixel coordinates to 0-1
      const normalizedX = bbox.originX / imgWidth;
      const normalizedY = bbox.originY / imgHeight;
      const normalizedWidth = bbox.width / imgWidth;
      const normalizedHeight = bbox.height / imgHeight;
      
      // STEP 2: Convert normalized coordinates to canvas coordinates
      face.boundingBox = {
        x: drawX + (normalizedX * drawWidth),
        y: drawY + (normalizedY * drawHeight),
        width: normalizedWidth * drawWidth,
        height: normalizedHeight * drawHeight
      };
    }
    
    // Convert keypoints — may be normalized (0-1) or pixel-space depending on API/version.
    if (detection.keypoints && Array.isArray(detection.keypoints)) {
      face.keypoints = detection.keypoints.map((point, idx) => {
        const px = Number(point.x);
        const py = Number(point.y);
        const { nx, ny } = toNormalizedCoord(px, py, imgWidth, imgHeight);
        return {
          id: idx,
          x: drawX + (nx * drawWidth),
          y: drawY + (ny * drawHeight),
          confidence: typeof point.confidence === 'number' ? point.confidence : 1.0,
          label: getKeypointLabel(idx)
        };
      });
    }
    
    return face;
  });
}

/**
 * Get human-readable keypoint labels
 * Based on MediaPipe Face Detector keypoint order
 * @param {number} index - Keypoint index
 * @returns {string} Keypoint label
 */
function getKeypointLabel(index) {
  const labels = [
    'right_eye',         // 0
    'left_eye',          // 1  
    'nose_tip',          // 2
    'mouth_center',      // 3
    'right_ear_tragion', // 4
    'left_ear_tragion'   // 5
  ];
  
  return labels[index] || `keypoint_${index}`;
}

/**
 * Check if MediaPipe is supported in current browser
 * @returns {boolean} True if supported
 */
export function isMediaPipeSupported() {
  try {
    // Check for required APIs
    const hasWebAssembly = typeof WebAssembly !== 'undefined';
    const hasWorker = typeof Worker !== 'undefined';
    const hasCanvas = typeof HTMLCanvasElement !== 'undefined';
    
    return hasWebAssembly && hasWorker && hasCanvas;
  } catch {
    return false;
  }
}

/**
 * Get MediaPipe initialization status
 * @returns {Object} Status information
 */
export function getMediaPipeStatus() {
  return {
    isInitialized: !!faceDetector,
    isInitializing,
    error: initializationError,
    isSupported: isMediaPipeSupported()
  };
}

/**
 * Cleanup MediaPipe resources
 * Call this when no longer needed to free memory
 */
export function cleanupMediaPipe() {
  if (faceDetector) {
    try {
      faceDetector.close();
    } catch (error) {
      console.warn('Error cleaning up MediaPipe:', error);
    }
    faceDetector = null;
  }
  initializationError = null;
  isInitializing = false;
}
